import { ChevronDown, Code2, Copy, FileText, Link, List } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { MetricPill } from './MetricPill'

interface ContentPreviewProps {
  content: string
  contentType?: 'text' | 'code' | 'list' | 'structured'
  wordCount?: number
  hasCodeBlocks?: boolean
  hasLinks?: boolean
  previewLength?: number
  className?: string
}

export function ContentPreview({
  content,
  contentType = 'text',
  wordCount = 0,
  hasCodeBlocks = false,
  hasLinks = false,
  previewLength = 300,
  className = '',
}: ContentPreviewProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isCopying, setIsCopying] = useState(false)

  const isLongContent = content.length > previewLength
  const displayContent =
    isExpanded || !isLongContent ? content : content.substring(0, previewLength) + '...'

  const handleCopy = async () => {
    try {
      setIsCopying(true)
      await navigator.clipboard.writeText(content)
      toast.success('Content copied to clipboard!', {
        autoClose: 2000,
      })
    } catch {
      toast.error('Failed to copy content', {
        autoClose: 2000,
      })
    } finally {
      setIsCopying(false)
    }
  }

  const getContentTypeIcon = (type: string) => {
    switch (type) {
      case 'code':
        return <Code2 className="h-3.5 w-3.5" />
      case 'list':
        return <List className="h-3.5 w-3.5" />
      case 'structured':
        return <FileText className="h-3.5 w-3.5" />
      default:
        return <FileText className="h-3.5 w-3.5" />
    }
  }

  const getContentTypeColor = (type: string) => {
    switch (type) {
      case 'code':
        return 'purple'
      case 'list':
        return 'blue'
      case 'structured':
        return 'green'
      default:
        return 'gray'
    }
  }

  return (
    <div className={`overflow-hidden rounded-2xl border border-gray-200/50 bg-white ${className}`}>
      {/* Header with metadata */}
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">Extracted Content</span>
          <MetricPill
            icon={getContentTypeIcon(contentType)}
            label={contentType}
            color={getContentTypeColor(contentType) as any}
            variant="subtle"
            size="sm"
          />
        </div>

        <div className="flex items-center gap-2">
          {/* Metadata pills */}
          <div className="flex gap-1.5">
            <MetricPill
              count={wordCount}
              label="words"
              color="gray"
              variant="subtle"
              size="sm"
              title={`${wordCount} words`}
            />

            {hasCodeBlocks && (
              <MetricPill
                icon={<Code2 className="h-3 w-3" />}
                color="purple"
                variant="subtle"
                size="sm"
                title="Contains code blocks"
              />
            )}

            {hasLinks && (
              <MetricPill
                icon={<Link className="h-3 w-3" />}
                color="blue"
                variant="subtle"
                size="sm"
                title="Contains links"
              />
            )}
          </div>

          {/* Copy button */}
          <button
            onClick={handleCopy}
            disabled={isCopying}
            className="group flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1 text-xs text-gray-600 transition-all hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50"
            title="Copy content"
          >
            <Copy
              className={`h-3 w-3 transition-transform ${isCopying ? 'scale-110' : 'group-hover:scale-110'}`}
            />
            {isCopying ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-3">
        <div className="leading-relaxed text-gray-900">
          <pre className="whitespace-pre-wrap break-words font-sans text-sm">{displayContent}</pre>
        </div>

        {/* Expand/Collapse button */}
        {isLongContent && (
          <div className="mt-3 flex justify-center">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="group flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-600 transition-all hover:bg-gray-50 hover:text-gray-700"
            >
              <span>{isExpanded ? 'Show Less' : 'Show More'}</span>
              <ChevronDown
                className={`h-3 w-3 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
              />
            </button>
          </div>
        )}
      </div>

      {/* Subtle gradient border */}
      <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-600/10 via-transparent to-purple-600/10 opacity-0 transition-opacity duration-300 hover:opacity-100" />
    </div>
  )
}
